import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';

const BranchPricingManager = () => {
    const { apiRequest } = useAdmin();
    const [branchPricing, setBranchPricing] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingBranch, setEditingBranch] = useState(null);
    const [formData, setFormData] = useState({});
    const [message, setMessage] = useState({ type: '', text: '' });
    const [forceRefresh, setForceRefresh] = useState(0);

    useEffect(() => {
        loadBranchPricing();
    }, [forceRefresh]);

    const loadBranchPricing = async () => {
        try {
            setLoading(true);
            const data = await apiRequest('/custom-packages/branch-pricing');
            console.log('Loaded branch pricing:', data.branchPricing);
            setBranchPricing(data.branchPricing || []);
        } catch (error) {
            setMessage({ type: 'error', text: 'Error loading branch pricing: ' + error.message });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (branch) => {
        setEditingBranch(branch.branch);

        // Convert distanceZones to object if it's a Map
        let distanceZonesObj = {};
        if (branch.distanceZones) {
            if (branch.distanceZones instanceof Map) {
                branch.distanceZones.forEach((value, key) => {
                    distanceZonesObj[key] = value;
                });
            } else if (typeof branch.distanceZones === 'object') {
                distanceZonesObj = { ...branch.distanceZones };
            }
        }

        setFormData({
            branch: branch.branch,
            fixedBasePrice: branch.fixedBasePrice || 1500,
            baseMultiplier: branch.baseMultiplier || 1,
            packageTypePrices: {
                family: branch.packageTypePrices?.family || 1000,
                couple: branch.packageTypePrices?.couple || 500,
                solo: branch.packageTypePrices?.solo || 300,
                group: branch.packageTypePrices?.group || 800,
                business: branch.packageTypePrices?.business || 1500
            },
            accommodationMultipliers: {
                standard: branch.accommodationMultipliers?.standard || 1,
                deluxe: branch.accommodationMultipliers?.deluxe || 1.2,
                luxury: branch.accommodationMultipliers?.luxury || 1.5,
                resort: branch.accommodationMultipliers?.resort || 2
            },
            transportPrices: {
                bus: branch.transportPrices?.bus || 100,
                train: branch.transportPrices?.train || 200,
                flight: branch.transportPrices?.flight || 500,
                private_car: branch.transportPrices?.private_car || 300
            },
            distanceZones: distanceZonesObj,
            description: branch.description || '',
            isActive: branch.isActive !== undefined ? branch.isActive : true
        });
    };

    const handleSave = async () => {
        try {
            setLoading(true);

            // ✅ FIXED: Include fixedBasePrice in submit data
            const submitData = {
                branch: formData.branch,
                fixedBasePrice: Number(formData.fixedBasePrice) || 0,
                baseMultiplier: Number(formData.baseMultiplier) || 1,
                packageTypePrices: {
                    family: Number(formData.packageTypePrices?.family) || 0,
                    couple: Number(formData.packageTypePrices?.couple) || 0,
                    solo: Number(formData.packageTypePrices?.solo) || 0,
                    group: Number(formData.packageTypePrices?.group) || 0,
                    business: Number(formData.packageTypePrices?.business) || 0
                },
                accommodationMultipliers: {
                    standard: Number(formData.accommodationMultipliers?.standard) || 1,
                    deluxe: Number(formData.accommodationMultipliers?.deluxe) || 1,
                    luxury: Number(formData.accommodationMultipliers?.luxury) || 1,
                    resort: Number(formData.accommodationMultipliers?.resort) || 1
                },
                transportPrices: {
                    bus: Number(formData.transportPrices?.bus) || 0,
                    train: Number(formData.transportPrices?.train) || 0,
                    flight: Number(formData.transportPrices?.flight) || 0,
                    private_car: Number(formData.transportPrices?.private_car) || 0
                },
                distanceZones: formData.distanceZones || {},
                description: formData.description || '',
                isActive: formData.isActive !== undefined ? formData.isActive : true
            };

            console.log('Submitting data:', submitData);

            const response = await apiRequest('/custom-packages/branch-pricing', {
                method: 'POST',
                body: submitData
            });

            console.log('Save response:', response);

            setMessage({
                type: 'success',
                text: `Branch pricing for ${formData.branch} saved successfully!`
            });

            // Reset form and refresh data
            setEditingBranch(null);
            setFormData({});

            // Force refresh by updating the dependency
            setForceRefresh(prev => prev + 1);

        } catch (error) {
            console.error('Save error:', error);
            setMessage({
                type: 'error',
                text: 'Error saving branch pricing: ' + (error.message || 'Unknown error')
            });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (branch) => {
        try {
            const response = await apiRequest(`/custom-packages/branch-pricing/${branch.branch}/toggle`, {
                method: 'PATCH'
            });

            setMessage({
                type: 'success',
                text: `Branch ${branch.branch} ${!branch.isActive ? 'activated' : 'deactivated'} successfully!`
            });

            // Force refresh
            setForceRefresh(prev => prev + 1);

        } catch (error) {
            setMessage({
                type: 'error',
                text: 'Error toggling branch: ' + error.message
            });
        }
    };

    const handleInputChange = (path, value) => {
        setFormData(prev => {
            const keys = path.split('.');
            const lastKey = keys.pop();
            const nested = keys.reduce((obj, key) => {
                obj[key] = obj[key] || {};
                return obj[key];
            }, prev);

            // Store the raw value, conversion will happen on submit
            nested[lastKey] = value;

            return { ...prev };
        });
    };

    // Initialize default pricing
    const initializeDefaultPricing = async () => {
        try {
            setLoading(true);
            const response = await apiRequest('/custom-packages/initialize-branch-pricing', {
                method: 'POST'
            });

            setMessage({
                type: 'success',
                text: 'Default branch pricing initialized successfully!'
            });

            // Force refresh
            setForceRefresh(prev => prev + 1);

        } catch (error) {
            setMessage({
                type: 'error',
                text: 'Error initializing pricing: ' + error.message
            });
        } finally {
            setLoading(false);
        }
    };

    // Clear message after 5 seconds
    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => {
                setMessage({ type: '', text: '' });
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    if (loading && branchPricing.length === 0) {
        return (
            <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading branch pricing...</p>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                            <h4 className="mb-0">
                                <i className="fas fa-money-bill-wave me-2"></i>
                                Branch Pricing Management
                            </h4>
                            {branchPricing.length === 0 && (
                                <button
                                    className="btn btn-warning"
                                    onClick={initializeDefaultPricing}
                                    disabled={loading}
                                >
                                    <i className="fas fa-plus me-1"></i>
                                    Initialize Default Pricing
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            {message.text && (
                                <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
                                    <strong>{message.type === 'success' ? 'Success!' : 'Error!'}</strong> {message.text}
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setMessage({ type: '', text: '' })}
                                    ></button>
                                </div>
                            )}

                            {branchPricing.map(branch => (
                                <div key={branch.branch} className="card mb-4">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0 text-capitalize">
                                            <i className="fas fa-building me-2"></i>
                                            {branch.branch} Branch Pricing
                                            {!branch.isActive && (
                                                <span className="badge bg-warning ms-2">Inactive</span>
                                            )}
                                        </h5>
                                        <div>
                                            <button
                                                className="btn btn-sm btn-outline-primary me-2"
                                                onClick={() => handleEdit(branch)}
                                                disabled={loading}
                                            >
                                                <i className="fas fa-edit me-1"></i>
                                                Edit Pricing
                                            </button>
                                            <button
                                                className={`btn btn-sm ${branch.isActive ? 'btn-warning' : 'btn-success'}`}
                                                onClick={() => handleToggleActive(branch)}
                                                disabled={loading}
                                            >
                                                <i className={`fas ${branch.isActive ? 'fa-pause' : 'fa-play'} me-1`}></i>
                                                {branch.isActive ? 'Deactivate' : 'Activate'}
                                            </button>
                                        </div>
                                    </div>

                                    {editingBranch === branch.branch ? (
                                        <div className="card-body">
                                            <EditBranchForm
                                                formData={formData}
                                                onInputChange={handleInputChange}
                                                onSave={handleSave}
                                                onCancel={() => {
                                                    setEditingBranch(null);
                                                    setFormData({});
                                                }}
                                                loading={loading}
                                            />
                                        </div>
                                    ) : (
                                        <div className="card-body">
                                            <BranchPricingDisplay branch={branch} />
                                        </div>
                                    )}
                                </div>
                            ))}

                            {branchPricing.length === 0 && (
                                <div className="text-center py-5">
                                    <i className="fas fa-money-bill-wave fa-3x text-muted mb-3"></i>
                                    <h5 className="text-muted">No Branch Pricing Configured</h5>
                                    <p className="text-muted">Click "Initialize Default Pricing" to set up Dhaka and Khulna branch pricing.</p>
                                    <button
                                        className="btn btn-warning mt-3"
                                        onClick={initializeDefaultPricing}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Initializing...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-plus me-1"></i>
                                                Initialize Default Pricing
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ✅ COMPLETE EditBranchForm Component
const EditBranchForm = ({ formData, onInputChange, onSave, onCancel, loading }) => {
    const districts = [
        'Bagerhat', 'Bandarban', 'Barguna', 'Barishal', 'Bhola', 'Bogra', 'Brahmanbaria',
        'Chandpur', 'Chattogram', 'Chuadanga', 'Cumilla', 'Cox\'s Bazar', 'Dhaka', 'Dinajpur',
        'Faridpur', 'Feni', 'Gaibandha', 'Gazipur', 'Gopalganj', 'Habiganj', 'Jamalpur',
        'Jashore', 'Jhalokati', 'Jhenaidah', 'Joypurhat', 'Khagrachhari', 'Khulna', 'Kishoreganj',
        'Kurigram', 'Kushtia', 'Lakshmipur', 'Lalmonirhat', 'Madaripur', 'Magura', 'Manikganj',
        'Meherpur', 'Moulvibazar', 'Munshiganj', 'Mymensingh', 'Naogaon', 'Narail', 'Narayanganj',
        'Narsingdi', 'Natore', 'Netrokona', 'Nilphamari', 'Noakhali', 'Pabna', 'Panchagarh',
        'Patuakhali', 'Pirojpur', 'Rajbari', 'Rajshahi', 'Rangamati', 'Rangpur', 'Satkhira',
        'Shariatpur', 'Sherpur', 'Sirajganj', 'Sunamganj', 'Sylhet', 'Tangail', 'Thakurgaon',
        'Chapainawabganj'
    ];

    return (
        <div>
            <div className="row mb-4">
                {/* Fixed Base Price Field */}
                <div className="col-md-4">
                    <label className="form-label fw-bold">Fixed Base Price (৳)</label>
                    <div className="input-group">
                        <span className="input-group-text">৳</span>
                        <input
                            type="number"
                            className="form-control"
                            value={formData.fixedBasePrice || ''}
                            onChange={(e) => onInputChange('fixedBasePrice', e.target.value)}
                            min="0"
                            step="100"
                        />
                    </div>
                    <small className="text-muted">
                        Fixed starting price for this branch (e.g., 1500 for Dhaka, 2000 for Khulna)
                    </small>
                </div>
                <div className="col-md-4">
                    <label className="form-label fw-bold">Base Multiplier</label>
                    <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        max="5"
                        className="form-control"
                        value={formData.baseMultiplier || ''}
                        onChange={(e) => onInputChange('baseMultiplier', e.target.value)}
                    />
                    <small className="text-muted">Multiplies total price after calculations</small>
                </div>
                <div className="col-md-4">
                    <label className="form-label fw-bold">Branch Description</label>
                    <input
                        type="text"
                        className="form-control"
                        value={formData.description || ''}
                        onChange={(e) => onInputChange('description', e.target.value)}
                        placeholder="Branch description that users will see..."
                    />
                </div>
            </div>

            <div className="row mb-4">
                <div className="col-12">
                    <h6 className="border-bottom pb-2 mb-3 fw-bold">
                        <i className="fas fa-box me-2"></i>
                        Package Type Base Prices
                    </h6>
                </div>
                {['family', 'couple', 'solo', 'group', 'business'].map(type => (
                    <div key={type} className="col-md-4 mb-3">
                        <label className="form-label text-capitalize fw-semibold">
                            {type} Package Base Price
                        </label>
                        <div className="input-group">
                            <span className="input-group-text">৳</span>
                            <input
                                type="number"
                                className="form-control"
                                value={formData.packageTypePrices?.[type] || ''}
                                onChange={(e) => onInputChange(`packageTypePrices.${type}`, e.target.value)}
                                min="0"
                                step="100"
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className="row mb-4">
                <div className="col-12">
                    <h6 className="border-bottom pb-2 mb-3 fw-bold">
                        <i className="fas fa-hotel me-2"></i>
                        Accommodation Multipliers
                    </h6>
                </div>
                {['standard', 'deluxe', 'luxury', 'resort'].map(type => (
                    <div key={type} className="col-md-3 mb-3">
                        <label className="form-label text-capitalize fw-semibold">
                            {type} Multiplier
                        </label>
                        <input
                            type="number"
                            step="0.1"
                            min="0.5"
                            max="3"
                            className="form-control"
                            value={formData.accommodationMultipliers?.[type] || ''}
                            onChange={(e) => onInputChange(`accommodationMultipliers.${type}`, e.target.value)}
                        />
                        <small className="text-muted">Multiplies base package price</small>
                    </div>
                ))}
            </div>

            <div className="row mb-4">
                <div className="col-12">
                    <h6 className="border-bottom pb-2 mb-3 fw-bold">
                        <i className="fas fa-bus me-2"></i>
                        Transport Prices (Per Person)
                    </h6>
                </div>
                {['bus', 'train', 'flight', 'private_car'].map(type => (
                    <div key={type} className="col-md-3 mb-3">
                        <label className="form-label text-capitalize fw-semibold">
                            {type.replace('_', ' ')} Price
                        </label>
                        <div className="input-group">
                            <span className="input-group-text">৳</span>
                            <input
                                type="number"
                                className="form-control"
                                value={formData.transportPrices?.[type] || ''}
                                onChange={(e) => onInputChange(`transportPrices.${type}`, e.target.value)}
                                min="0"
                                step="50"
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className="row mb-4">
                <div className="col-12">
                    <h6 className="border-bottom pb-2 mb-3 fw-bold">
                        <i className="fas fa-map-marker-alt me-2"></i>
                        Distance Zone Prices (From This Branch)
                    </h6>
                    <p className="text-muted mb-3">
                        Set additional cost for each district based on distance from this branch.
                    </p>
                </div>

                {/* First 12 districts */}
                <div className="col-12 mb-3">
                    <h6 className="text-primary">Popular Districts</h6>
                </div>
                {districts.slice(0, 12).map(district => (
                    <div key={district} className="col-md-3 mb-2">
                        <label className="form-label small fw-semibold">{district}</label>
                        <div className="input-group input-group-sm">
                            <span className="input-group-text">৳</span>
                            <input
                                type="number"
                                className="form-control"
                                value={formData.distanceZones?.[district] || ''}
                                onChange={(e) => onInputChange(`distanceZones.${district}`, e.target.value)}
                                placeholder="0"
                                min="0"
                                step="50"
                            />
                        </div>
                    </div>
                ))}

                {/* Remaining districts in collapsible section */}
                <div className="col-12 mt-3">
                    <div className="accordion" id="moreDistrictsAccordion">
                        <div className="accordion-item">
                            <h2 className="accordion-header">
                                <button
                                    className="accordion-button collapsed"
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#moreDistricts"
                                >
                                    <i className="fas fa-chevron-down me-2"></i>
                                    Show All 64 Districts ({districts.length - 12} more)
                                </button>
                            </h2>
                            <div id="moreDistricts" className="accordion-collapse collapse" data-bs-parent="#moreDistrictsAccordion">
                                <div className="accordion-body">
                                    <div className="row">
                                        {districts.slice(12).map(district => (
                                            <div key={district} className="col-md-3 mb-2">
                                                <label className="form-label small fw-semibold">{district}</label>
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text">৳</span>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={formData.distanceZones?.[district] || ''}
                                                        onChange={(e) => onInputChange(`distanceZones.${district}`, e.target.value)}
                                                        placeholder="0"
                                                        min="0"
                                                        step="50"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-4 pt-3 border-top">
                <button
                    className="btn btn-primary me-2"
                    onClick={onSave}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Saving...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-save me-2"></i>
                            Save Changes
                        </>
                    )}
                </button>
                <button
                    className="btn btn-secondary"
                    onClick={onCancel}
                    disabled={loading}
                >
                    <i className="fas fa-times me-2"></i>
                    Cancel
                </button>
            </div>
        </div>
    );
};

// ✅ COMPLETE BranchPricingDisplay Component
const BranchPricingDisplay = ({ branch }) => {
    const formatCurrency = (amount) => {
        return `৳${amount?.toLocaleString() || '0'}`;
    };

    // Convert distanceZones to array for display
    let distanceZonesArray = [];
    if (branch.distanceZones) {
        if (branch.distanceZones instanceof Map) {
            distanceZonesArray = Array.from(branch.distanceZones.entries());
        } else if (typeof branch.distanceZones === 'object') {
            distanceZonesArray = Object.entries(branch.distanceZones);
        }
    }

    return (
        <div>
            <div className="row mb-4">
                <div className="col-md-6">
                    <div className="card bg-light">
                        <div className="card-body">
                            <h6 className="card-title">
                                <i className="fas fa-info-circle me-2 text-primary"></i>
                                Branch Information
                            </h6>
                            <div className="row">
                                <div className="col-6">
                                    <strong>Fixed Base Price:</strong>
                                </div>
                                <div className="col-6">
                                    <span className="badge bg-success fs-6">{formatCurrency(branch.fixedBasePrice)}</span>
                                </div>
                                <div className="col-6 mt-2">
                                    <strong>Base Multiplier:</strong>
                                </div>
                                <div className="col-6 mt-2">
                                    <span className="badge bg-primary fs-6">{branch.baseMultiplier}x</span>
                                </div>
                                <div className="col-12 mt-2">
                                    <strong>Description:</strong>
                                    <p className="mb-0 text-muted">{branch.description || 'No description provided'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card bg-light">
                        <div className="card-body">
                            <h6 className="card-title">
                                <i className="fas fa-chart-line me-2 text-success"></i>
                                Status
                            </h6>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className={branch.isActive ? 'text-success' : 'text-warning'}>
                                    <i className={`fas ${branch.isActive ? 'fa-check-circle' : 'fa-pause-circle'} me-2`}></i>
                                    {branch.isActive ? 'Active' : 'Inactive'}
                                </span>
                                <span className="text-muted small">
                                    Updated: {new Date(branch.updatedAt).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row mb-4">
                <div className="col-12">
                    <h6 className="border-bottom pb-2 mb-3 fw-bold text-primary">
                        <i className="fas fa-box me-2"></i>
                        Package Type Prices
                    </h6>
                    <div className="row">
                        {Object.entries(branch.packageTypePrices || {}).map(([type, price]) => (
                            <div key={type} className="col-md-2 mb-2">
                                <div className="card h-100">
                                    <div className="card-body text-center p-3">
                                        <div className="text-capitalize small fw-bold text-muted">{type}</div>
                                        <div className="h5 mb-0 text-success">{formatCurrency(price)}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="row mb-4">
                <div className="col-md-6">
                    <h6 className="border-bottom pb-2 mb-3 fw-bold text-primary">
                        <i className="fas fa-hotel me-2"></i>
                        Accommodation Multipliers
                    </h6>
                    <div className="row">
                        {Object.entries(branch.accommodationMultipliers || {}).map(([type, multiplier]) => (
                            <div key={type} className="col-6 mb-2">
                                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                                    <span className="text-capitalize small">{type}</span>
                                    <span className="badge bg-info">{multiplier}x</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="col-md-6">
                    <h6 className="border-bottom pb-2 mb-3 fw-bold text-primary">
                        <i className="fas fa-bus me-2"></i>
                        Transport Prices (Per Person)
                    </h6>
                    <div className="row">
                        {Object.entries(branch.transportPrices || {}).map(([type, price]) => (
                            <div key={type} className="col-6 mb-2">
                                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                                    <span className="text-capitalize small">{type.replace('_', ' ')}</span>
                                    <span className="badge bg-warning text-dark">{formatCurrency(price)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <h6 className="border-bottom pb-2 mb-3 fw-bold text-primary">
                        <i className="fas fa-map-marker-alt me-2"></i>
                        Distance Zone Prices
                        <small className="text-muted ms-2">({distanceZonesArray.length} districts configured)</small>
                    </h6>

                    {/* Show top 8 districts by price */}
                    <div className="row">
                        {distanceZonesArray
                            .sort((a, b) => b[1] - a[1])
                            .slice(0, 8)
                            .map(([district, price]) => (
                                <div key={district} className="col-md-3 mb-2">
                                    <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                                        <span className="small">{district}</span>
                                        <span className="badge bg-secondary">{formatCurrency(price)}</span>
                                    </div>
                                </div>
                            ))
                        }
                    </div>

                    {/* Show remaining districts in collapsible section */}
                    {distanceZonesArray.length > 8 && (
                        <div className="mt-3">
                            <div className="accordion" id="distanceZonesAccordion">
                                <div className="accordion-item">
                                    <h2 className="accordion-header">
                                        <button
                                            className="accordion-button collapsed"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#allDistanceZones"
                                        >
                                            <i className="fas fa-chevron-down me-2"></i>
                                            Show All {distanceZonesArray.length} Districts
                                        </button>
                                    </h2>
                                    <div id="allDistanceZones" className="accordion-collapse collapse" data-bs-parent="#distanceZonesAccordion">
                                        <div className="accordion-body">
                                            <div className="row">
                                                {distanceZonesArray.map(([district, price]) => (
                                                    <div key={district} className="col-md-3 mb-2">
                                                        <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                                                            <span className="small">{district}</span>
                                                            <span className="badge bg-secondary">{formatCurrency(price)}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BranchPricingManager;